import { isDefined } from '@sgme/fp';
import classnames from 'classnames';
import { PanelGroup } from 'components/shared/PanelGroup/PanelGroup';
import { WithToggle } from 'components/shared/WithToggle/WithToggle';
import { useToggle } from 'hooks/useToggle';
import { Collapse, Modal, ModalBody } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import type { KycDetail, KycScoreStatus } from 'state/secondaryMarket/shared/kyc/kyc.model';

interface KycScoreProps {
  details: readonly KycDetail[];
  status: KycScoreStatus;
}

export function KycScoreErrorModal({ details, status }: KycScoreProps) {
  const { isOn, toggle } = useToggle();
  const { formatMessage } = useIntl();

  const color = mapStatusToColor(status);

  return (
    <div className="d-flex">
      <span className="flex-grow-1"> {formatMessage({ id: 'secondaryMarket.sidePanel.kyc' })}</span>
      <span className={`text-${color}`}>{formatMessage({ id: `secondaryMarket.sidePanel.kyc.${status}` })}</span>
      <a data-e2e="kyc-details-label" className={`ms-3 text-link clickable text-${color}`} onClick={toggle}>
        <FormattedMessage id="details" />
      </a>
      <Modal show={isOn} onHide={toggle} centered data-e2e="kyc-error-details-modal">
        <KycErrorModalHeader errorsCount={details.length} toggle={toggle} />
        <KycErrorModalBody details={details} status={status} />
      </Modal>
    </div>
  );
}

function mapStatusToColor(status: KycScoreStatus) {
  switch (status) {
    case 'green':
      return 'success';
    case 'red':
      return 'danger';
    case 'restricted':
      return 'warning';
  }
}

type KycColor = ReturnType<typeof mapStatusToColor>;

interface KycErrorModalHeaderProps {
  errorsCount: number;
  toggle(): void;
}

function KycErrorModalHeader({ errorsCount, toggle }: KycErrorModalHeaderProps) {
  return (
    <div className="modal-header pb-3">
      <h3 className="modal-title">
        <FormattedMessage id="secondaryMarket.sidePanel.kycStatus" />
        <span className="badge rounded-pill badge-discreet-primary badge-sm align-middle ms-2">{errorsCount}</span>
      </h3>
      <button
        aria-label="Close"
        className="btn-close"
        data-dismiss="modal"
        data-e2e="kyc-details-close-button"
        onClick={toggle}
        type="button"
      />
    </div>
  );
}

function KycErrorModalBody({ details, status }: KycScoreProps) {
  const color = mapStatusToColor(status);

  return (
    <ModalBody>
      <div
        className={classnames('overflow-y-auto', {
          'has-only-one-error': details.length === 1,
        })}
        data-e2e="kyc-details-modal-body"
        id="kyc-details-modal-body"
      >
        <PanelGroup e2e="kyc-details-modal-panel-group" id="kyc-details-modal-panel-group">
          {details.map((detail, index: number) => (
            <WithToggle isInitiallyOn key={index}>
              {({ isOn, toggle }) => (
                <div
                  className={classnames({
                    'mt-2': index > 0,
                    'border-danger border': isOn && color === 'danger',
                    'border-warning border': isOn && color === 'warning',
                  })}
                  data-e2e={`kyc-details-modal-panel-${index}`}
                >
                  <div className="card bg-lvl1">
                    <div className="card-header p-0">
                      <button
                        className={`btn clickable shadow-none justify-content-start collapsed w-100 d-flex align-items-center text-${color} bg-${color} bg-opacity-10 p-3`}
                        onClick={toggle}
                      >
                        <i className="icon icon-sm ms-0 me-3">cancel</i>
                        <span className="me-3">
                          <FormattedMessage id="secondaryMarket.sidePanel.kyc.label" />
                        </span>
                        <span>
                          <FormattedMessage id={`secondaryMarket.sidePanel.kyc.${status}`} />
                        </span>
                        <span className="ms-auto fw-bold">
                          <FormattedMessage id={isOn ? 'hide' : 'details'} tagName="u" />
                        </span>
                      </button>
                    </div>

                    <Collapse in={isOn}>
                      <div className="card-body py-0 px-2">
                        <div className="list-group list-group-flush">
                          <KycErrorDetailLine type="reason" value={detail.reason} index={index} color={color} />
                          <KycErrorDetailLine type="action" value={detail.action} index={index} color={color} />
                        </div>
                      </div>
                    </Collapse>
                  </div>
                </div>
              )}
            </WithToggle>
          ))}
        </PanelGroup>
      </div>
    </ModalBody>
  );
}

interface KycErrorDetailLineProps {
  type: keyof KycDetail;
  value?: string;
  index: number;
  color: KycColor;
}

function KycErrorDetailLine({ type, value, index, color }: KycErrorDetailLineProps) {
  if (!isDefined(value)) {
    return null;
  }
  return (
    <li className="list-group-item border-bottom d-md-flex justify-content-md-between flex-wrap px-0 py-2">
      <div className="text-secondary" data-e2e={`kyc-details-modal-panel-${index}-${type}-key`}>
        <FormattedMessage id={`secondaryMarket.sidePanel.kyc.detail.${type}`} />
      </div>
      <div className={`w-100 text-${color}`} data-e2e={`kyc-details-modal-panel-${index}-${type}-value`}>
        {value}
      </div>
    </li>
  );
}
